/**
 * Centralized group store fetching: single source of truth for /api/group/store/:_id.
 * Only one in-flight request per id; results cached and dispatched so all consumers get data without duplicate API calls.
 * The actual API call is performed by a fetcher registered from utils/api/users.js (URL lives there).
 */
let fetcher = null

/**
 * Register the function that performs the group/store get API call. Called from users.js.
 * @param { (id: string) => Promise<response> } fn
 */
export function registerFetcher(fn) {
  fetcher = typeof fn === 'function' ? fn : null
}

const cacheById = {}
const inFlightById = {}

const EVENT_REQUEST = 'groupStoreRequest'
const EVENT_LOADED = 'groupStoreLoaded'

function dispatchLoaded(id, response) {
  try {
    window.dispatchEvent(
      new CustomEvent(EVENT_LOADED, {
        detail: { id, response },
      })
    )
  } catch (e) {
    // ignore
  }
}

function handleGroupStoreRequest(e) {
  const id = e?.detail?.id != null ? String(e.detail.id) : ''
  if (!id) return

  if (cacheById[id]) {
    dispatchLoaded(id, cacheById[id])
    return
  }
  if (inFlightById[id]) {
    return
  }
  if (!fetcher) {
    return
  }

  const promise = fetcher(id)
    .then((res) => {
      cacheById[id] = res
      dispatchLoaded(id, res)
      return res
    })
    .catch((err) => {
      dispatchLoaded(id, null)
      throw err
    })
    .finally(() => {
      delete inFlightById[id]
    })

  inFlightById[id] = promise
}

if (typeof window !== 'undefined') {
  window.addEventListener(EVENT_REQUEST, handleGroupStoreRequest)
}

/**
 * Request group store for a user id. Dispatches groupStoreRequest; returns a promise that resolves when groupStoreLoaded fires for this id.
 * All callers share the same in-flight request and cache.
 */
export function getGroupStore(id) {
  const key = id != null ? String(id) : ''
  if (!key) return Promise.reject(new Error('id is required'))

  if (cacheById[key]) {
    return Promise.resolve(cacheById[key])
  }

  return new Promise((resolve, reject) => {
    const handler = (e) => {
      if (e.detail?.id !== key) return
      window.removeEventListener(EVENT_LOADED, handler)
      const response = e.detail?.response
      if (response != null) {
        resolve(response)
      } else {
        reject(new Error('Group store load failed'))
      }
    }
    window.addEventListener(EVENT_LOADED, handler)
    window.dispatchEvent(new CustomEvent(EVENT_REQUEST, { detail: { id: key } }))
  })
}

/**
 * Get cached group store response for an id (no fetch, no dispatch).
 */
export function getCachedGroupStore(id) {
  const key = id != null ? String(id) : ''
  return key ? cacheById[key] ?? null : null
}

/**
 * Clear cache so next request will refetch. Call with no args to clear all, or pass an id to clear one.
 */
export function clearGroupStoreCache(id) {
  if (id != null && id !== '') {
    delete cacheById[String(id)]
  } else {
    Object.keys(cacheById).forEach((k) => delete cacheById[k])
  }
}

export { EVENT_REQUEST, EVENT_LOADED }
